import React, { useContext, useEffect, useState } from 'react'
import { join } from 'lodash'
import MaskedInput from 'react-maskedinput'
import Select from 'live/components/forms/ui/select'
import i18n from 'live/services/i18n'
import { STATIC_DATA_STATES } from '../../../chart-components'
import {
    InformationIconComponent,
    ModalField,
    CustomMeasureSelect,
    CustomDatePicker,
    PipeConnectionROPSectionHeader,
    MissingInformationSectionArea
} from '../../../modal-components'
import { RestrictedPerformanceHeader } from '../../../modal-components-style'
import { RestrictedRateOfPenetrationErrors } from '../../types'
import { dateAndTimeRegex, dateTimeToTimestamp, timestampToDateTime } from '../../../../utils/parse'
import { RawActivityEvent, RestrictedRateOfPenetration, SelectOptions, Uom, ValueUom } from '../../../../utils/type'
import { ModalLineContainer, RestrictedSection } from '../../ActivityModal'
import { RESTRICTED_PERFORMANCE_COLUMN } from '../../constants'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { generateRestrictedGoalValue } from '../../../../context/utils'
import { StaticDataContext } from '../../../../context/StaticDataContext'

interface RestrictedRateOfPenetrationInformationProps {
    restrictedRateOfPenetration: RestrictedRateOfPenetration
    index: number
    tubularEvent: RawActivityEvent
    deleteRestrictedRateOfPenetration: (index: number) => void
    filtersAppliedToGoals: {}
    setFiltersAppliedToGoals: (filtersAppliedToGoals) => void
    errors: RestrictedRateOfPenetrationErrors
    ROPSpeedOptions: ValueUom[] | null
    loadingROPSpeedOptions: boolean
    restrictedRopTypeList: SelectOptions[]
    fetchROPSpeedOptions: () => void
    updateCurrentRestrictedRateOfPenetration: (restrictedIndex: number, key: string, newValue: any) => void
    assetFilterId?: string
    tubularsIntervalPerTime: boolean
    lengthUnitOptions: Uom[]
    setIsAddRestrictedPerformanceButtonDisabled: (addRestrictedPerformanceButtonDisabled: boolean) => void
}

export const RestrictedRateOfPenetrationInformation = ({
    restrictedRateOfPenetration,
    index,
    tubularEvent,
    deleteRestrictedRateOfPenetration,
    filtersAppliedToGoals,
    setFiltersAppliedToGoals,
    errors,
    ROPSpeedOptions,
    loadingROPSpeedOptions,
    restrictedRopTypeList,
    fetchROPSpeedOptions,
    updateCurrentRestrictedRateOfPenetration,
    assetFilterId,
    tubularsIntervalPerTime,
    lengthUnitOptions,
    setIsAddRestrictedPerformanceButtonDisabled
}: RestrictedRateOfPenetrationInformationProps) => {
    const eventStartDateTime = restrictedRateOfPenetration
        ? restrictedRateOfPenetration.start
            ? timestampToDateTime(restrictedRateOfPenetration.start)
            : null
        : null
    const eventEndDateTime = restrictedRateOfPenetration
        ? restrictedRateOfPenetration.end
            ? timestampToDateTime(restrictedRateOfPenetration.end)
            : null
        : null
    const [startDate, setStartDate] = useState<string | null>(eventStartDateTime?.date || null)
    const [startHour, setStartHour] = useState<string | null>(eventStartDateTime?.time || null)
    const [endDate, setEndDate] = useState<string | null>(eventEndDateTime?.date || null)
    const [endHour, setEndHour] = useState<string | null>(eventEndDateTime?.time || null)
    const { liveTheme } = useContext(StaticDataContext)

    const isRestrictedEvent = event => event?.restrictedRopType && !['UNRESTRICTED'].includes(event?.restrictedRopType)

    const fieldDisabledAfterEntityStart = tubularEvent.state !== STATIC_DATA_STATES.EMPTY

    const onChangeRestrictedROPProperty = (restrictedIndex: number, key: string, newValue: any) => {
        updateCurrentRestrictedRateOfPenetration(restrictedIndex, key, newValue)
    }

    const updateRestrictedROPGoalToEmpty = (restrictedIndex: number) => {
        onChangeRestrictedROPProperty(restrictedIndex, 'restrictedRopSpeed', {
            ropSpeed: '',
            uom: null,
            value: null
        })
        onChangeRestrictedROPProperty(restrictedIndex, 'restrictedRopSpeedAcronym', '')
    }

    useEffect(() => {
        if (!!restrictedRateOfPenetration.restrictedRopSpeed) {
            if (tubularsIntervalPerTime) {
                setIsAddRestrictedPerformanceButtonDisabled(!restrictedRateOfPenetration.end)
            } else {
                setIsAddRestrictedPerformanceButtonDisabled(!restrictedRateOfPenetration.finalMD?.value)
            }
        }
    }, [restrictedRateOfPenetration.end, restrictedRateOfPenetration.finalMD?.value])

    useEffect(() => {
        const datetime = join([startDate, startHour], ' ')
        if (!startHour || dateAndTimeRegex.test(datetime)) {
            const timestamp = dateTimeToTimestamp(datetime)

            if (timestamp) {
                onChangeRestrictedROPProperty(index, 'start', timestamp)
            }
        }
    }, [startDate, startHour])

    useEffect(() => {
        const datetime = join([endDate, endHour], ' ')
        if (!endHour || dateAndTimeRegex.test(datetime)) {
            const timestamp = dateTimeToTimestamp(datetime)

            if (timestamp) {
                onChangeRestrictedROPProperty(index, 'end', timestamp)
            }
        }
    }, [endDate, endHour])

    useEffect(() => {
        if (assetFilterId && filtersAppliedToGoals && restrictedRateOfPenetration.restrictedRopType) {
            fetchROPSpeedOptions()
        }
    }, [assetFilterId, filtersAppliedToGoals, restrictedRateOfPenetration.restrictedRopType])

    return (
        <RestrictedSection>
            <div key={index}>
                <RestrictedPerformanceHeader>
                    <PipeConnectionROPSectionHeader label="Restricted Rate of Penetration: " quantity={index + 1} />

                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip style={{ zIndex: 9999 }} id={''}>
                                {i18n('Delete Restricted Performance')}
                            </Tooltip>
                        }
                    >
                        <i
                            className="far fa-trash-alt"
                            onClick={() => {
                                deleteRestrictedRateOfPenetration(index)
                                setIsAddRestrictedPerformanceButtonDisabled(false)
                            }}
                        />
                    </OverlayTrigger>
                </RestrictedPerformanceHeader>
                <ModalField
                    label="Restricted ROP"
                    style={{ paddingTop: '15px' }}
                    field={
                        <Select
                            searchable={false}
                            clearable={false}
                            options={restrictedRopTypeList.map(option => ({
                                label: i18n(option.label),
                                value: option.value
                            }))}
                            value={restrictedRateOfPenetration.restrictedRopType}
                            onChange={value => {
                                const newValue = restrictedRopTypeList?.find(type => type?.value === value)

                                setFiltersAppliedToGoals(filters => ({
                                    ...filters,
                                    [RESTRICTED_PERFORMANCE_COLUMN]: newValue?.acronym
                                }))
                                onChangeRestrictedROPProperty(index, 'restrictedRopType', newValue?.value)
                                updateRestrictedROPGoalToEmpty(index)
                            }}
                            placeholder={
                                restrictedRopTypeList?.find(
                                    type => type?.value === restrictedRateOfPenetration.restrictedRopType
                                )?.label
                            }
                            style={errors?.restrictedRopType ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                <ModalField
                    label="Restricted ROP Goal"
                    style={{ paddingTop: '15px' }}
                    field={
                        <Select
                            searchable={false}
                            clearable={false}
                            disabled={!isRestrictedEvent(restrictedRateOfPenetration)}
                            options={ROPSpeedOptions?.map(option => generateRestrictedGoalValue(option))}
                            placeholder={i18n(isRestrictedEvent(restrictedRateOfPenetration) ? 'Select...' : '-')}
                            value={
                                restrictedRateOfPenetration.restrictedRopSpeed?.value &&
                                generateRestrictedGoalValue(
                                    restrictedRateOfPenetration.restrictedRopSpeed,
                                    restrictedRateOfPenetration.restrictedRopSpeedAcronym
                                )
                            }
                            onChange={restrictedRopSpeed => {
                                onChangeRestrictedROPProperty(index, 'restrictedRopSpeed', restrictedRopSpeed)
                                onChangeRestrictedROPProperty(
                                    index,
                                    'restrictedRopSpeedAcronym',
                                    restrictedRopSpeed.ropSpeed
                                )
                            }}
                            style={errors?.restrictedRopSpeed ? { borderColor: '#D9534F' } : {}}
                        />
                    }
                />
                {isRestrictedEvent(restrictedRateOfPenetration) &&
                    ROPSpeedOptions?.length === 0 &&
                    !restrictedRateOfPenetration.restrictedRopSpeed?.value && (
                        <MissingInformationSectionArea
                            label="Missing information"
                            onRefresh={fetchROPSpeedOptions}
                            isLoading={loadingROPSpeedOptions}
                            LinkToRigPage={assetFilterId && `#/rig/${assetFilterId}/kpi-options`}
                        >
                            <p>
                                {i18n('There is no KPI Goal applied for this Restricted Performance')}{' '}
                                <strong>ROP</strong>. {i18n('You must set a value by going to')}{' '}
                                <a href={assetFilterId && `#/rig/${assetFilterId}/kpi-options`} target="_blank">
                                    {i18n('Rig KPI Goals')}
                                </a>
                                .
                            </p>
                            <p>
                                {i18n(
                                    'In the KPI Goals list, define as many Restricted Performance goals as you need for the'
                                )}{' '}
                                <strong>{i18n('KPI ROP per Stand')}</strong> {i18n('for tubular')}{' '}
                                <strong>Drill Pipe</strong>.
                            </p>
                            <p>
                                {i18n('After saving, return to this modal. The values will be loaded into the')}{' '}
                                <strong>{i18n('Restricted ROP Speed')}</strong> {i18n('field.')}
                            </p>
                        </MissingInformationSectionArea>
                    )}
                {tubularsIntervalPerTime && (
                    <>
                        <ModalField
                            style={{ paddingTop: '15px' }}
                            field={
                                <div className="input-date-time">
                                    <ModalField
                                        label="Start Date"
                                        labelAditionalField={InformationIconComponent(
                                            "You must fill this field with a value greater than or equal to the tubular's Start Date/Hour value (when it is the first Restricted Performance) or greater than the End Date/Hour of the previous Restricted Performance.This checkbox enabled defines that the first tubular will have its depth extended to the greatest bit depth, allowing the automatic adjustment of the Final MD to the same value of Run Final MD. If disabled, there will be no classification of the tubular from the maximum bit depth."
                                        )}
                                        field={
                                            <CustomDatePicker
                                                style={
                                                    errors?.start
                                                        ? {
                                                              borderColor: '#D9534F'
                                                          }
                                                        : undefined
                                                }
                                                isDisabled={!restrictedRateOfPenetration.restrictedRopSpeed?.value}
                                                date={isRestrictedEvent(restrictedRateOfPenetration) ? startDate : null}
                                                setDate={setStartDate}
                                            />
                                        }
                                    />
                                    <ModalField
                                        label="Start Hour"
                                        field={
                                            <MaskedInput
                                                style={{
                                                    ...(errors?.start ? { borderColor: '#D9534F' } : {}),
                                                    paddingLeft: '8px'
                                                }}
                                                mask="11:11"
                                                placeholder="00:00"
                                                disabled={!restrictedRateOfPenetration.restrictedRopSpeed?.value}
                                                value={startHour}
                                                onChange={e => setStartHour(e.target.value)}
                                            />
                                        }
                                    />
                                </div>
                            }
                        />
                        <ModalField
                            style={{ paddingTop: '15px' }}
                            field={
                                <div className="input-date-time">
                                    <ModalField
                                        label="End Date"
                                        labelAditionalField={InformationIconComponent(
                                            'This field is automatically filled with the End Date/Hour from the previous tubular.'
                                        )}
                                        field={
                                            <CustomDatePicker
                                                style={
                                                    errors?.end
                                                        ? {
                                                              borderColor: '#D9534F'
                                                          }
                                                        : undefined
                                                }
                                                isDisabled={!restrictedRateOfPenetration.restrictedRopSpeed?.value}
                                                date={isRestrictedEvent(restrictedRateOfPenetration) ? endDate : null}
                                                setDate={setEndDate}
                                            />
                                        }
                                    />
                                    <ModalField
                                        label="End Hour"
                                        field={
                                            <MaskedInput
                                                style={{
                                                    ...(errors?.end ? { borderColor: '#D9534F' } : {}),
                                                    paddingLeft: '8px'
                                                }}
                                                mask="11:11"
                                                placeholder="00:00"
                                                disabled={!restrictedRateOfPenetration.restrictedRopSpeed?.value}
                                                value={endHour}
                                                onChange={e => setEndHour(e.target.value)}
                                            />
                                        }
                                    />
                                </div>
                            }
                        />
                    </>
                )}
                {!tubularsIntervalPerTime && (
                    <ModalLineContainer style={{ paddingTop: '15px' }}>
                        <ModalField
                            label="Start MD"
                            labelAditionalField={
                                <>
                                    <CustomMeasureSelect
                                        setSelectedUomValue={uomValue => {
                                            onChangeRestrictedROPProperty(index, 'startMD', uomValue)
                                        }}
                                        disabled={!restrictedRateOfPenetration.restrictedRopSpeed?.value}
                                        selectedOptionValues={restrictedRateOfPenetration.startMD}
                                        options={lengthUnitOptions}
                                    />
                                    {InformationIconComponent(
                                        'You must fill this field with a value greater than or equal to the Tubular Start MD value and greater than the previous Restricted Pipe Movement Type Final MD.'
                                    )}
                                </>
                            }
                            field={
                                <div className="input-normal">
                                    <input
                                        type="number"
                                        disabled={!restrictedRateOfPenetration.restrictedRopSpeed?.value}
                                        value={restrictedRateOfPenetration?.startMD?.value}
                                        onChange={e => {
                                            const startMDValue = e.target?.value ? Number(e.target.value) : undefined

                                            const startMD = {
                                                ...restrictedRateOfPenetration?.startMD,
                                                value: startMDValue
                                            }
                                            onChangeRestrictedROPProperty(index, 'startMD', startMD)
                                        }}
                                        style={
                                            errors?.startMD
                                                ? {
                                                      borderColor: '#D9534F'
                                                  }
                                                : undefined
                                        }
                                        placeholder="0.00"
                                    />
                                </div>
                            }
                        />
                        <ModalField
                            label="Final MD"
                            labelAditionalField={
                                <>
                                    <CustomMeasureSelect
                                        setSelectedUomValue={uomValue => {
                                            onChangeRestrictedROPProperty(index, 'finalMD', uomValue)
                                        }}
                                        disabled={!restrictedRateOfPenetration.restrictedRopSpeed?.value}
                                        selectedOptionValues={restrictedRateOfPenetration.finalMD}
                                        options={lengthUnitOptions}
                                    />
                                    {InformationIconComponent(
                                        'You must fill this field with a value less than or equal to the Run Final MD value and greater than the Start MD.'
                                    )}
                                </>
                            }
                            field={
                                <div className="input-normal">
                                    <input
                                        type="number"
                                        disabled={!restrictedRateOfPenetration.restrictedRopSpeed?.value}
                                        value={restrictedRateOfPenetration.finalMD?.value}
                                        onChange={e => {
                                            const finalMDValue = e.target?.value ? Number(e.target.value) : undefined

                                            const finalMD = {
                                                ...restrictedRateOfPenetration?.finalMD,
                                                value: finalMDValue
                                            }
                                            onChangeRestrictedROPProperty(index, 'finalMD', finalMD)
                                        }}
                                        style={
                                            errors?.finalMD
                                                ? {
                                                      borderColor: '#D9534F'
                                                  }
                                                : undefined
                                        }
                                        placeholder="0.00"
                                    />
                                </div>
                            }
                        />
                    </ModalLineContainer>
                )}
            </div>
        </RestrictedSection>
    )
}
